import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { formatFixed } from "../../utilities/format";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { RootState } from "../../state/store";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import OperationsTable from "./OperationsTable";
import TablePage from "../../components/Layout/TablePage";
import SaveButton from "../../components/Buttons/SaveButton";
import Button from "../../components/Buttons/Button";
import { useGlobalModal } from "../../components/Contexts/GlobalModalContext";
import MultipleStoppagesModal from "./MultipleStoppagesModal";
import { OptionResponse } from "../../DTOs/Common/OptionResponse";
import { stoppageTypesQuery } from "../../components/Hooks/useStoppageTypes";
import { StoppageTypeDto } from "../../DTOs/Stoppage/StoppageTypeDto";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { TripleShift } from "../../types/Shift";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { enqueue, hasQueuedCompletion } from "../../offline/queueOps";
import { queryClient } from "../../queryClient";
import { computeShiftId } from "../../offline/computeShiftId";
import { isOnlineNow } from "../../offline/connectivity";
import { withTimeout } from "../../offline/withTimeout";
import {
  EARLIEST_COMPLETION_MINUTES_BEFORE_START,
  createProvisionalNextShift,
  minutesBeforeNextShiftStart,
} from "../../offline/provisionalShift";
import { useIsReadOnly } from "../../utilities/helpers/roleUtilities";
import i18n from "../../i18n";
import { viewName } from "../../i18n/pageNames";

const Operations: React.FC = () => {
  const { t } = useTranslation("production");
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isReadOnly = useIsReadOnly();
  const isStoppageReadOnly = useIsReadOnly("Stoppages");
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift);
  const { selectedDate, selectedShift } = useShiftTimes();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)
  const dispatch = useDispatch();
  const { showModal, hideModal } = useGlobalModal();
  const { enqueueSnackbar } = useSnackbar();
  const [refreshFn, setRefreshFn] = useState<() => void>(() => () => { });
  const {
    options: locationOptions,
    isLoading: isLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const triggerRefresh = useCallback(() => {
    refreshFn?.();
  }, [refreshFn]);

  const setLocalSelectedShift = useCallback((newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  }, [dispatch]);

  const showMultipleStoppagesModal = async () => {
    showModal({
      content: () => (<MultipleStoppagesModal
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        selectedDate={selectedDate}
        selectedShift={localSelectedShift}
        closeModal={hideModal}
        refreshPage={triggerRefresh}
      />
      ),
      tailwindMaxWidthClass: 'max-w-2xl'
    });
    return true;
  };

  useEffect(() => {
    const newView = "Operations";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const { data: stoppageTypes = new OptionResponse<StoppageTypeDto>([]) } = useQuery(stoppageTypesQuery);

  const stoppageTypeGroupOptions = stoppageTypes.groups.flatMap(group => group.options.map(o => ({
    value: o.key,
    label: o.label,
    groupColor: group.fullObject?.colourHex ?? '',
    stoppageType: group.label,
    downtimeTypeId: group.key
  })));

  const showOfflineCompletionModal = useCallback((message: string) => {
    showModal({
      content: ({ close }) => (
        <div>
          <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
            {t("operations.noNetwork")}
          </h2>
          <p className="!text-theme-text">{message}</p>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="primary" onClick={close}>{t("operations.ok")}</Button>
          </div>
        </div>
      ),
      tailwindMaxWidthClass: 'max-w-md',
    })
  }, [showModal, t])

  const completeShiftOffline = async (): Promise<boolean> => {
    const currentDate = selectedDate.toISODate()!
    const isLatestShift = (latestShiftDate === currentDate && latestShift === localSelectedShift)
      || (provisionalShiftDate === currentDate && provisionalShift === localSelectedShift)

    if (!isLatestShift) {
      showOfflineCompletionModal(t("operations.onlyLatestOffline"))
      return false
    }

    const minutesEarly = minutesBeforeNextShiftStart(currentDate, localSelectedShift)
    if (minutesEarly > EARLIEST_COMPLETION_MINUTES_BEFORE_START) {
      showOfflineCompletionModal(minutesEarly > 90
        ? t("operations.tooEarlyHours", { hours: formatFixed(minutesEarly / 60, 1), limit: EARLIEST_COMPLETION_MINUTES_BEFORE_START })
        : t("operations.tooEarlyMinutes", { minutes: Math.round(minutesEarly), limit: EARLIEST_COMPLETION_MINUTES_BEFORE_START }))
      return false
    }

    showModal({
      content: ({ close }) => (
        <div>
          <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
            {t("operations.completeOfflineTitle")}
          </h2>
          <p className="!text-theme-text">
            {localSelectedShift === 'DAY'
              ? t("operations.completeOfflineDay", { date: currentDate })
              : t("operations.completeOfflineNight", { date: currentDate })}
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="secondary" onClick={close}>{i18n.t("actions.cancel")}</Button>
            <Button
              variant="success"
              onClick={() => {
                close()
                void queueOfflineCompletion(currentDate)
              }}
            >
              {t("operations.completeShift")}
            </Button>
          </div>
        </div>
      ),
      tailwindMaxWidthClass: 'max-w-md',
    })

    return true
  }

  const queueOfflineCompletion = async (currentDate: string): Promise<void> => {
    const shiftId = computeShiftId(currentDate, localSelectedShift)
    let stage = 'reading the offline queue'

    try {
      if (!await withTimeout(hasQueuedCompletion(shiftId), stage)) {
        stage = 'queueing the completion'
        await withTimeout(enqueue({
          entity: 'completeShift',
          operation: 'create',
          shiftId,
        }), stage)
      }

      stage = 'creating the new shift on this device'
      const provisional = await withTimeout(createProvisionalNextShift(currentDate, localSelectedShift), stage)
      showOfflineCompletionModal(provisional.operationsCarried > 0
        ? t("operations.provisionalCreated", { machines: provisional.operationsCarried, patterns: provisional.patternsCarried })
        : t("operations.queuedWithoutCache"))
      dispatch(setSelectedDate(provisional.date))
      setLocalSelectedShift(provisional.shift)
    } catch (error) {
      console.error(`Offline shift completion failed while ${stage}`, error)
      const detail = error instanceof Error ? error.message : String(error)
      showOfflineCompletionModal(stage === 'creating the new shift on this device'
        ? t("operations.newShiftFailed", { detail })
        : stage === 'reading the offline queue'
          ? t("operations.failedReadingQueue", { detail })
          : t("operations.failedQueueing", { detail }))
    }
  }

  const completeShift = async () => {
    if (!isOnlineNow()) return completeShiftOffline();

    let res: Awaited<ReturnType<typeof operationsAjax.completeShift>>;
    try {
      res = await operationsAjax.completeShift(selectedDate, localSelectedShift);
    } catch (error) {
      console.error('Complete shift request did not reach the server; completing offline instead', error);
      return completeShiftOffline();
    }

    if (!res.errors && !res.data) {
      enqueueSnackbar(t("operations.shiftCompleted"), { variant: 'success', autoHideDuration: 3000 });
      await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] });
      return true;
    }
    res.errors?.forEach((error, index) => {
      enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 });
    });
    return false;
  };

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={(date) => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={viewName("Operations")} />
      </Breadcrumbs>
      <div className="flex flex-row gap-1 items-center justify-center shrink-0">
        <SaveButton
          buttonLabel={t("operations.multipleStoppage")}
          requireConfirmation={false}
          handleSave={showMultipleStoppagesModal}
          disabled={isStoppageReadOnly}
          styleClasses="compact:py-1"
        />
        <SaveButton
          buttonLabel={t("operations.completeShift")}
          requireConfirmation={false}
          handleSave={completeShift}
          disabled={isReadOnly}
          styleClasses="compact:py-1"
        />
      </div>
      <OperationsTable
        selectedShift={localSelectedShift}
        selectedDate={selectedDate}
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        locationOptions={locationOptions}
        isLocationOptionsLoading={isLocationOptionsLoading}
        setRefreshFn={setRefreshFn}
      />
    </TablePage>
  );
};

export default Operations;